import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * The "updating" page for a tab that is already open (ADR 0030 amendment).
 *
 * The edge serves `updating.html` as a 503 while a deploy is under way and whenever the API does not answer. A page
 * load gets that page; a tab already open only gets it as the answer to its own requests, and used to draw each one
 * as an error ("the server answered 503") on whatever screen was showing. So the client recognises that answer, says
 * "Joogoos is updating" once, across the top of every screen, asks `/health` every few seconds until the API is back,
 * then re-asks what the screen shows. When the build that came back is not the one this tab started on, it offers a
 * reload rather than doing one: the person may be halfway through typing something.
 *
 * The API itself only ever answers in JSON, so a 502, 503 or 504 that is not JSON is the edge (or Cloudflare) speaking.
 */
export type UpdateState = "running" | "updating" | "updated";

const POLL_MS = 3000;

let state: UpdateState = "running";
let startedOn: string | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function set(next: UpdateState) {
  if (next === state) return;
  state = next;
  for (const l of listeners) l();
}

export function isEdgeAnswer(res: Response): boolean {
  return [502, 503, 504].includes(res.status) && !(res.headers.get("content-type") ?? "").includes("json");
}

/** The build this tab is drawing, as `/health` said before anything went wrong. Only the first answer counts. */
export function rememberBuild(sha: string | undefined) {
  if (sha && !startedOn) startedOn = sha;
}

async function poll() {
  timer = undefined;
  const sha = await fetch("/health", { headers: { accept: "application/json" } })
    .then(async (r) => (r.ok && !isEdgeAnswer(r) ? ((await r.json()) as { sha?: string }).sha ?? "" : undefined))
    .catch(() => undefined);
  if (sha === undefined) { timer = setTimeout(() => void poll(), POLL_MS); return; }
  set(startedOn && sha && sha !== startedOn ? "updated" : "running");
}

/** A request came back as the edge's page: say so, and start waiting for the API. Returns whether it was. */
export function noteAnswer(res: Response): boolean {
  if (!isEdgeAnswer(res)) return false;
  if (state !== "updating") { set("updating"); if (!timer) timer = setTimeout(() => void poll(), POLL_MS); }
  return true;
}

/** Load time: which build this tab is. One request, so there is something to compare with after an update. */
export function watchForUpdates() {
  void fetch("/health", { headers: { accept: "application/json" } })
    .then((r) => (r.ok ? (r.json() as Promise<{ sha?: string }>) : undefined))
    .then((h) => rememberBuild(h?.sha))
    .catch(() => undefined);
}

export const updateState = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; },
  /** Tests only: back to a fresh tab. */
  reset: () => { if (timer) clearTimeout(timer); timer = undefined; startedOn = undefined; state = "running"; listeners.clear(); },
};

export const UPDATING_TEXT = "Joogoos is updating. Back in a moment.";
export const UPDATED_TEXT = "A new version of Joogoos is live.";

/**
 * Mounted beside the app in `main.tsx`, like the test-server label, so every screen shows it. A status, not an
 * alert: it announces itself politely and never steals focus. Classes only, because the policy allows no inline style.
 */
export function UpdatingBanner({ reload = () => location.reload() }: { reload?: () => void }) {
  const now = useSyncExternalStore(updateState.subscribe, updateState.get);
  const qc = useQueryClient();
  const was = useRef(now);
  useEffect(() => {
    // Back: everything that failed while the API was away is asked again, so no screen is left showing an error.
    if (was.current === "updating" && now !== "updating") void qc.invalidateQueries();
    was.current = now;
  }, [now, qc]);

  if (now === "running") return null;
  return (
    <div role="status" className="fixed top-3 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-full bg-ink px-4 py-1.5 text-[13px] font-medium text-page shadow">
      {now === "updating" ? (
        <>
          <span className="size-2 rounded-full bg-coral animate-pulse" aria-hidden />
          {UPDATING_TEXT}
        </>
      ) : (
        <>
          {UPDATED_TEXT}
          <button type="button" onClick={reload} className="rounded-full bg-coral px-2.5 py-0.5 text-[12px] font-semibold text-page hover:bg-coral-2">
            Reload
          </button>
        </>
      )}
    </div>
  );
}
